import { FC, useState } from 'react';
import Drawer from '..';
import DrawerHeader from '../header';
import { Box } from '@mui/material';
import DrawerContent from '../content';
import { useQuery } from 'react-query';
import { ViewState } from '@/lib/helpers';
import useQueryHelper from '@/hooks/use-query-helper';
import useFormHandle from '@/hooks/use-form-handle.hook';
import {
  PartnerRoleLabels,
  PartnerRoleOptions,
  PartnerUserModel,
  PartnerUserRole,
} from '@/lib/models/partner-user.model';
import ReadonlyView from '../../form/readonly-view';
import InputControl from '../../form/input-control';
import DrawerMenu from '../menu';
import UpdateMenuItems from '../menu/update-menu-items';
import useToast from '@/hooks/use-toast.hook';
import useModal from '@/hooks/use-modal.hook';
import DrawerButtons from '@/components/form/drawer-buttons';
import {
  createPartnerMember,
  createPartnerUser,
  deletePartnerUser,
  getPartnerUserById,
  isPartnerUserDomainInvalid,
  patchPartnerUser,
} from '@/lib/services/partner-user.service';
import { ReadonlyField } from '@/components/form/readonly-view/helpers';
import { QueryKey } from '@/lib/query-client';
import { Form } from '@/components/form';
import { FieldError } from '@/lib/services';
import { DrawerProps } from '../helpers';
import { getPartner, getPartnerById } from '@/lib/services/partner.service';
import Chip from '@/components/chip';
import InlineToast from '@/components/toasts/inline';
import { userCan } from '@/lib/services/auth.service';
import useApp from '@/hooks/use-app.hook';

interface Props extends DrawerProps {
  id: string | null;
  partnerId?: string;
}

const fields: ReadonlyField[] = [
  { label: 'Name', prop: 'name' },
  { label: 'Email', prop: 'email' },
  { label: 'Role', prop: 'role' },
];

const formModel = new PartnerUserModel();

const PartnerUserDrawer: FC<Props> = ({
  id,
  partnerId,
  open,
  onClose,
  onChange,
  className,
  isTopDrawer,
}) => {
  const { user } = useApp();
  const canManageAdmins = partnerId ? userCan(user, 'super') : userCan(user, 'manage-admins');
  const [userId, setUserId] = useState(id || null);
  const isNew = !userId;
  const [mode, setMode] = useState<ViewState>(isNew ? 'add' : 'view');
  const inWriteMode = ['edit', 'add'].includes(mode);

  const { toast, errorToast } = useToast();
  const { openModal } = useModal();

  const query = useQuery(
    [QueryKey.PartnerUserView, partnerId, userId],
    async () => getPartnerUserById(userId!, partnerId),
    {
      enabled: !isNew,
    }
  );
  const { showChildren: showUserChildren } = useQueryHelper(query);
  const { data, refetch } = query;
  const partnerUser = data || new PartnerUserModel();

  const { name: partnerUserName, email: partnerUserEmail, emailDomain } = partnerUser;

  const partnerQuery = useQuery([QueryKey.PartnerProfile, partnerId], async () =>
    partnerId ? getPartnerById(partnerId) : getPartner()
  );
  const { data: partner } = partnerQuery;
  const { domainUiOptions } = partner || {};
  const { showChildren: showPartnerChildren } = useQueryHelper(partnerQuery);

  // For new users, show form when partner data is ready; for existing users, show when user data is ready
  const showChildren = isNew ? showPartnerChildren : showUserChildren;

  const domainInvalid = isPartnerUserDomainInvalid(partnerUser, partner || null);

  const formHandle = useFormHandle({
    initialValues: formModel,
    validationSchema: formModel.schema,
    validateOnChange: !isNew,
    onSubmit: async (user, { setFieldError }) => {
      const setFieldErrors = (fieldErrors: FieldError[]) => {
        fieldErrors.forEach(({ msg, path }) => setFieldError(path, msg));
      };

      if (!userId) {
        const { created, model, error, fieldErrors } = partnerId
          ? await createPartnerMember(partnerId, user)
          : await createPartnerUser(user);

        if (fieldErrors) {
          setFieldErrors(fieldErrors);
          return;
        }

        if (error || !created) {
          errorToast(error || 'The partner admin was not created');
          return;
        }

        toast('The partner admin was created');
        setUserId(model?.id || '');
        onChange?.();
        setMode('view');

        return;
      }
      // @todo editing your own account? need to update app context, etc.

      const { patched, fieldErrors, error } = await patchPartnerUser(
        user.id,
        user.propSlice(['name', 'email', 'role']),
        partnerId
      );

      if (fieldErrors) {
        setFieldErrors(fieldErrors);
        return;
      }

      if (error || !patched) {
        errorToast(error || 'The partner admin was not updated');
        return;
      }

      toast('The partner admin was updated');
      refetch();
      onChange && onChange();
      setMode('view');
    },
  });

  const { handleSubmit, canSubmit, isSubmitting, loadData } = formHandle;

  const handleCancel = () => {
    if (isNew) {
      onClose();
      return;
    }
    setMode('view');
    loadData(partnerUser);
  };

  const handleEdit = () => {
    setMode('edit');
    loadData(partnerUser);
  };

  const handleDelete = () => {
    openModal('confirm', {
      title: 'Confirm Delete',
      content: `Are you sure you want to delete ${partnerUserEmail}?`,
      onClose: (confirm: boolean) => {
        if (confirm) {
          deletePartnerUser(partnerUser, partnerId)
            .then(({ deleted, error }) => {
              if (deleted) {
                setUserId('');
                onClose();
                toast('The partner admin was deleted');
                onChange?.();
                return;
              }

              errorToast(error || 'The partner admin was not deleted');
            })
            .catch(() => errorToast('The partner admin was not deleted'));
        }
      },
    });
  };

  const handleReadonlyData = (data: Record<string, unknown>) => {
    const email = data.email as string;

    return {
      ...data,
      role: PartnerRoleLabels[data.role as PartnerUserRole],
      email: (
        <Box display="flex" alignItems="center" gap={1}>
          {email}
          {domainInvalid && <Chip label="Invalid Domain" color="error" size="small" />}
        </Box>
      ),
    };
  };

  return (
    <Drawer open={open} onClose={onClose} className={className} query={query}>
      <DrawerHeader
        onClose={onClose}
        isTopDrawer={isTopDrawer}
        menu={
          !isNew && canManageAdmins ? (
            <DrawerMenu>
              <UpdateMenuItems
                onEdit={handleEdit}
                onDelete={handleDelete}
                canEdit={!inWriteMode}
                canDelete={!inWriteMode}
              />
            </DrawerMenu>
          ) : undefined
        }
      >
        {isNew ? 'Add Partner Admin' : partnerUserName}
      </DrawerHeader>
      <DrawerContent>
        {showChildren && (
          <Box pt={2}>
            {!inWriteMode ? (
              <>
                <ReadonlyView
                  fields={fields}
                  data={partnerUser._props}
                  onData={handleReadonlyData}
                />
                {domainInvalid && (
                  <InlineToast
                    level="error"
                    message={`This email domain ${emailDomain} is not valid for this organization. Please update the email or contact support to add this domain to your organization.`}
                  />
                )}
              </>
            ) : (
              <Form formHandle={formHandle} query={query}>
                <InputControl name="name" label="Name" formHandle={formHandle} autoFocus />

                <InputControl
                  name="email"
                  type="email-with-domain"
                  options={domainUiOptions}
                  formHandle={formHandle}
                />
                <InputControl
                  name="role"
                  label="Role"
                  type="select"
                  options={PartnerRoleOptions}
                  formHandle={formHandle}
                />

                <DrawerButtons
                  submit
                  cancel
                  isNew={isNew}
                  canSubmit={canSubmit}
                  canCancel={!isSubmitting}
                  onSubmit={handleSubmit}
                  onCancel={handleCancel}
                />
              </Form>
            )}
          </Box>
        )}
      </DrawerContent>
    </Drawer>
  );
};

export default PartnerUserDrawer;
